import { atom, useAtom } from "jotai";
import {
  BookOpen,
  BookText,
  ExternalLink,
  Newspaper,
  PhoneIcon,
} from "lucide-react";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "src/components/ui/breadcrumb";
import { Button } from "src/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "src/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "src/components/ui/drawer";
import { ScrollArea } from "src/components/ui/scroll-area";
import { useIsMobile } from "src/lib/hooks/useMobile";
import { cn } from "src/lib/utils";

import HowToX from "./HowToX";
import HowToY from "./HowToY";
import HowToZ from "./HowToZ";

export const supportDialogAtom = atom({
  open: false,
  activeTab: "howToX",
});

const tabs = [
  { id: "howToX", label: "How to X", icon: BookOpen },
  { id: "howToY", label: "How to Y", icon: BookText },
  { id: "howToZ", label: "How to Z", icon: Newspaper },
];

const SupportDialog = () => {
  const [dialogState, setDialogState] = useAtom(supportDialogAtom);
  const isMobile = useIsMobile();

  const { open, activeTab } = dialogState;
  const setOpen = (open) => {
    setDialogState({
      ...dialogState,
      open,
    });
  };

  const renderContent = () => {
    switch (activeTab) {
      case "howToX":
        return <HowToX />;
      case "howToY":
        return <HowToY />;
      case "howToZ":
        return <HowToZ />;
      default:
        return null;
    }
  };

  const renderInnerContent = () => (
    <>
      <div
        className={cn(
          "border-b",
          isMobile
            ? "min-h-fit self-stretch overflow-x-auto"
            : "md:w-48 md:shrink-0 md:border-r md:border-b-0"
        )}
      >
        <nav
          className={cn(
            "flex gap-1",
            isMobile
              ? "flex-row overflow-x-auto px-2 pt-1 pb-2"
              : "p-4 md:h-full md:flex-col"
          )}
        >
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setDialogState({
                    ...dialogState,
                    activeTab: tab.id,
                  });
                }}
                className={cn(
                  "flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium whitespace-nowrap transition-colors",
                  activeTab === tab.id
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                <Icon className="size-4" />
                <span className={cn(isMobile ? "inline" : "hidden md:inline")}>
                  {tab.label}
                </span>
              </button>
            );
          })}
        </nav>
      </div>

      <div className="flex min-h-0 flex-1 flex-col pr-3 pb-3">
        <div className="hidden border-b pt-4 pb-4 md:block">
          <div className="flex items-center justify-between">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbPage>Support</BreadcrumbPage>
                  <BreadcrumbSeparator />
                </BreadcrumbItem>
                <BreadcrumbItem>
                  <BreadcrumbPage>
                    {tabs.find((t) => t.id === activeTab)?.label}
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            <Button
              size="sm"
              className="mr-10 flex items-center gap-1.5"
              asChild
            >
              <a
                href="https://calendly.com/youroptimizator/discovery-call"
                target="_blank"
                rel="noopener noreferrer"
              >
                <PhoneIcon className="size-3" />
                Schedule a Call
                <ExternalLink className="size-3" />
              </a>
            </Button>
          </div>
        </div>

        <ScrollArea className="-mr-3 flex-1 py-4 pr-4 pl-3 md:pl-0">
          <div className="space-y-4">{renderContent()}</div>
        </ScrollArea>
      </div>
    </>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent className="h-full max-h-[85vh]">
          <DrawerHeader className="pb-0">
            <div className="flex items-center justify-between">
              <DrawerTitle>Support</DrawerTitle>
              <Button
                size="sm"
                className="my-auto flex items-center gap-1.5"
                variant="outline"
                asChild
              >
                <a
                  href="https://calendly.com/youroptimizator/discovery-call"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <PhoneIcon className="size-3" />
                  <span className="sr-only md:not-sr-only">
                    Schedule a Call
                  </span>
                  <ExternalLink className="size-3" />
                </a>
              </Button>
            </div>
          </DrawerHeader>
          {renderInnerContent()}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="flex h-[75dvh] max-w-4xl overflow-hidden rounded-lg p-0 sm:max-w-4xl"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">Support</DialogTitle>
        <DialogDescription className="sr-only">
          Find help and support resources
        </DialogDescription>
        {renderInnerContent()}
      </DialogContent>
    </Dialog>
  );
};

export default SupportDialog;
